import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  BoxFull,
  TransactionalSubheader,
} from "iuvity-ui-components";
import { Header } from "../../components";
import { ProdGeneralInfo } from "../../components/products/ProdGeneralInfo";
import { ProdDataTableActions } from "../../components/products/ProdDatatableActions";
import {
  NewCustomDialog,
  NewDialogProps,
} from "../../components/dialog/NewCustomDialog";
import { useProductFields } from "../../hooks/useProductFields";
import { AppContext } from "../../context";
import { useProducts } from "../../hooks/useProducts";
import { propertyKeyLabel } from "../../utils/index";

export const ProductConfigurationPage = () => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user },
  } = useContext(AppContext);

  const {
    data: productsData,
    productKeysToProperties,
    productAction,
  } = useProducts("ID");
  const { productFieldAction } = useProductFields();

  const [modalProps, setModalProps] = useState<NewDialogProps>({
    visible: false,
  } as NewDialogProps);

  const formOptions = useMemo(() => {
    if (productsData && productsData[0].productproperties) {
      return {
        product: productsData[0].productproperties
          .filter((prop) => productKeysToProperties.includes(prop.key))
          .map((prop) => ({
            ...prop,
            label:
              propertyKeyLabel(
                t,
                "Products.",
                prop?.key === "name" ||
                  prop?.key === "labelkey" ||
                  prop?.key === "status"
                  ? `prod${prop?.key}`
                  : prop?.key
              ) || "",
          })),
        productproperties: productsData[0].productproperties
          .filter((prop) => !productKeysToProperties.includes(prop.key))
          .map((prop) => ({
            ...prop,
            label: t(`Products.${prop?.label || `${prop?.key}Label`}`),
          }))
          .sort(
            (a, b) =>
              b?.type.localeCompare(a?.type) || a?.label.localeCompare(b.label)
          ),
      };
    } else {
      return;
    }
  }, [t, productsData, productKeysToProperties]);

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}
      <TransactionalSubheader
        subheaderTitle={
          <div>
            <i
              className="iu iu-icon-left pr-3"
              onClick={() => navigate("/products")}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {t("Products.configProductLabel")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("Products.configProductLabel")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Products.productAdminTitle"),
              command: () => navigate("/products"),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: t("Products.configProductLabel"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
        <div className="t-subheader-content">
          <span>{t("Products.configDescription1Label")}</span>
          <span>{t("Products.configDescription2Label")}</span>
        </div>
      </TransactionalSubheader>

      <BoxFull style={{ background: "transparent" }}>
        <div className="inputlabel h4 my-5">
          {t("Products.productInformationLabel")}
        </div>

        <Accordion
          activeIndex={0}
          id="accordion"
          items={[
            {
              children: (
                <ProdGeneralInfo
                  type="PRODUCT"
                  data={formOptions?.product}
                  actionFn={productAction}
                  auxModalController={setModalProps}
                  dropdownFilterObject={{ labelkey: true }}
                />
              ),
              disabled: false,
              header: t("Products.generalInfoLabel"),
              key: "1001",
            },
            {
              children: (
                <ProdGeneralInfo
                  type="PROPERTIES"
                  data={formOptions?.productproperties}
                  actionFn={productFieldAction}
                  auxModalController={setModalProps}
                />
              ),
              disabled: false,
              header: t("Products.propsInfoLabel"),
              key: "1002",
            },
            {
              children: (
                <ProdDataTableActions
                  usageType="mainProductDetails"
                  serviceApi="productBalance"
                  auxModalController={setModalProps}
                />
              ),
              disabled: false,
              header: t("Products.producBasicInfoLabel"),
              key: "1003",
            },
            {
              children: (
                <ProdDataTableActions
                  usageType="additionalProductDetails"
                  serviceApi="productDetail"
                  auxModalController={setModalProps}
                />
              ),
              disabled: false,
              header: t("Products.moreInfoLabel"),
              key: "1004",
            },
          ]}
        />

        <div className="inputlabel h4 my-5">{t("Products.movementsLabel")}</div>

        <Accordion
          id="accordion"
          items={[
            {
              children: (
                <ProdDataTableActions
                  usageType="transactionsList"
                  serviceApi="transactions"
                  auxModalController={setModalProps}
                />
              ),
              disabled: false,
              header: t("Products.basiInfoLabel"),
              key: "1005",
            },
            {
              children: (
                <ProdDataTableActions
                  usageType="transactionsDetails"
                  serviceApi="transactions"
                  auxModalController={setModalProps}
                />
              ),
              disabled: false,
              header: t("Products.detailInfoLabel"),
              key: "1006",
            },
            {
              children: (
                <ProdDataTableActions
                  usageType="creditDebitNoteDetails"
                  serviceApi="transactions"
                  auxModalController={setModalProps}
                />
              ),
              disabled: false,
              header: t("Products.debidCreditInfoLabel"),
              key: "1007",
            },
          ]}
        />

        <div className="inputlabel h4 my-5">{t("Products.dashboardLabel")}</div>

        <Accordion
          id="accordion"
          items={[
            {
              children: (
                <ProdDataTableActions
                  usageType="dashboardProducts"
                  serviceApi="productBalance"
                  dashboardOption={true}
                  isEditable={false}
                  limit={5}
                  auxModalController={setModalProps}
                />
              ),
              disabled: false,
              header: t("Products.productsLabel"),
              key: "1007",
            },
            {
              children: (
                <ProdDataTableActions
                  usageType="lastTransactions"
                  serviceApi="transactions"
                  auxModalController={setModalProps}
                />
              ),
              disabled: false,
              header: t("Products.lastTransactionsLabel"),
              key: "1008",
            },
          ]}
        />

        <div className="inputlabel h4 my-5">
          {t("Products.otherSectionsLabel")}
        </div>

        <Accordion
          id="accordion"
          items={[
            {
              children: (
                <ProdDataTableActions
                  usageType="dashboardConsolidated"
                  serviceApi="productBalance"
                  isEditable={false}
                  limit={1}
                  auxModalController={setModalProps}
                />
              ),
              disabled: false,
              header: t("Products.consolidatedBalancesLabel"),
              key: "1009",
            },
          ]}
        />
      </BoxFull>

      <NewCustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
        aditionalClass="edit-property-page-dialog overlay-createlabelsbutton"
        acceptLabel={modalProps.acceptLabel}
        rejectLabel={modalProps.rejectLabel}
        onReject={modalProps.onReject}
      />
    </>
  );
};
